import React, { useContext, useState, useEffect } from 'react';
import './Payment.css';
import { PaymentContext } from "../../context/PaymentContext.js";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import Loader from '../../components/Loader.js';
import { useToast } from '../../context/ToastContext.js';
import {
  validateTCKN,
  validateBirthDate,
  validateCardForm,
  validateInvoiceForm,
  formatBirthDate,
  formatPhoneNumber,
  formatExpiryDate
} from '../../lib/validators.js';

Modal.setAppElement('#root');

const Payment = () => {
  const {
    state: { cardNumber, totalAmount, cardHolderName, expiryDate, cvc, installments, commissionRate, selectedInstallment, error, paymentSuccess },
    setCardNumber,
    setCardHolderName,
    setExpiryDate,
    setCvc,
    setTotalAmount,
    fetchCommissionRates,
    fetchInstallments,
    setSelectedInstallment,
    processPayment,
    calculateMonthlyPayment,
    confirmEftPayment
  } = useContext(PaymentContext);
  const location = useLocation();
  const { total, customerForm, cart } = location.state || {};
  const [isOpen, setIsOpen] = useState(false);
  const [isFormActive, setIsFormActive] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedInstallmentData, setSelectedInstallmentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  // Kart animasyonu için state'ler
  const [isCvcFocused, setIsCvcFocused] = useState(false);

  // EFT/Havale ödemesi için state'ler
  const [orderCreated, setOrderCreated] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderAmount, setOrderAmount] = useState(0);
  const [copySuccess, setCopySuccess] = useState('');
  const [eftData, setEftData] = useState(null);
  const [eftDiscount, setEftDiscount] = useState(0); // EFT indirimi için

  const [invoiceData, setInvoiceData] = useState({
    ad: '',
    soyad: '',
    adres: '',
    dogumTarihi: '',
    tckimlik: '',
    telefon: '05'
  });
  const [invoiceErrors, setInvoiceErrors] = useState({});

  const [paymentState, setPaymentState] = useState({
    success: false,
    orderId: null,
    amount: null
  });

  const [isPaymentCompleteModalOpen, setIsPaymentCompleteModalOpen] = useState(false);

  const isEftPayment = customerForm && customerForm.paymentType === 'eft';

  // Sarrafiye ürünleri kontrolü
  const hasSarrafiyeProducts = cart && cart.some(product => product.sarrafiye == 1);

  const handleFocus = () => setIsFormActive(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (total) {
      setTotalAmount(total);

      if (isEftPayment) {
        const discountAmount = total * 0.02;
        setEftDiscount(discountAmount);
      }
    }
    fetchCommissionRates();
  }, []);

  useEffect(() => {
    if (cardNumber.length == 8 || cardNumber.length == 16) {
      fetchInstallments(cardNumber);
    } else {
      setSelectedInstallment(null);
    }
  }, [cardNumber]);

  // Taksit seçenekleri yüklenmediyse varsayılan 1 taksit seç
  useEffect(() => {
    if (cardNumber.length >= 8 && (!installments || installments.length === 0) && !selectedInstallment && !isEftPayment) {
      setSelectedInstallment(1);
      setSelectedInstallmentData({ count: 1, rate: 0, totalAmount: total });
    }
  }, [cardNumber, installments, selectedInstallment, isEftPayment, total]);

  // EFT verilerini getir
  useEffect(() => {
    if (isEftPayment && orderCreated) {
      const fetchEftData = async () => {
        try {
          const response = await fetch('https://api.bikuyum.com/api/get_eft');
          const data = await response.json();
          if (data && data.length > 0) {
            setEftData(data[0]);
          }
        } catch (error) {
          showToast('EFT bilgileri şu anda alınamıyor', 'error')
          console.error('EFT bilgileri alınırken hata oluştu:', error);
        }
      };

      fetchEftData();
    }
  }, [isEftPayment, orderCreated]);

  // Kredi kartı validasyon fonksiyonu, artık validators.js'den alınacak
  const validateForm = () => {
    return validateCardForm({
      cardNumber,
      cardHolderName,
      expiryDate,
      cvc,
      selectedInstallmentData
    });
  };

  const handleCardNumberChange = (e) => {
    let newCardNumber = e.target.value.replace(/\D/g, '');
    if (newCardNumber.length > 0) {
      newCardNumber = newCardNumber.slice(0, 16);
    }
    setCardNumber(newCardNumber);
    // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
    setErrors(prev => ({ ...prev, cardNumber: '' }));
  };

  const handleInstallmentChange = (e) => {
    const selectedValue = Number(e.target.value);
    setSelectedInstallment(selectedValue);
    const selected = installments.find(inst => inst.count === selectedValue);
    setSelectedInstallmentData(selected);
    setErrors(prev => ({ ...prev, installment: '' }));
  };

  const handleCardHolderNameChange = (e) => {
    setCardHolderName(e.target.value);
    // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
    setErrors(prev => ({ ...prev, cardHolderName: '' }));
  };

  const handleExpiryDateChange = (e) => {
    const formattedDate = formatExpiryDate(e.target.value);
    setExpiryDate(formattedDate);
    // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
    setErrors(prev => ({ ...prev, expiryDate: '' }));
  };

  useEffect(() => {
    if (total) {
      setTotalAmount(total);
    }
  }, [total]);

  const handleCvcChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 3);
    setCvc(value);
    setErrors(prev => ({ ...prev, cvc: '' }));
  };

  // Doğum tarihi işleme fonksiyonu
  const handleBirthDateChange = (e) => {
    const formattedDate = formatBirthDate(e.target.value);
    setInvoiceData(prev => ({ ...prev, dogumTarihi: formattedDate }));
    // Hata mesajını temizle
    setInvoiceErrors(prev => ({ ...prev, dogumTarihi: '' }));
  };

  // Fatura formu doğrulama fonksiyonu
  const validateInvoiceFormData = () => {
    return validateInvoiceForm(invoiceData);
  };

  // Fatura bilgisi değişikliği için handler
  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;

    if (name === 'tckimlik') {
      if (value[0] === '0') return;
      if (/^\d*$/.test(value) && value.length <= 11) {
        setInvoiceData(prev => ({ ...prev, [name]: value }));
        // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
        setInvoiceErrors(prev => ({ ...prev, [name]: '' }));
      }
      return;
    }

    if (name === 'telefon') {
      handlePhoneInput(e);
      // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
      setInvoiceErrors(prev => ({ ...prev, [name]: '' }));
      return;
    }

    if (name === 'dogumTarihi') {
      handleBirthDateChange(e);
      return;
    }

    if (name === 'ad' || name === 'soyad') {
      const sanitizedValue = value.replace(/\./g, '');
      setInvoiceData(prev => ({ ...prev, [name]: sanitizedValue }));
      // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
      setInvoiceErrors(prev => ({ ...prev, [name]: '' }));
      return;
    }

    setInvoiceData(prev => ({ ...prev, [name]: value }));
    // Hata mesajını temizle, validasyon sadece form gönderiminde yapılacak
    setInvoiceErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePhoneInput = (e) => {
    const { value } = e.target;
    const formattedPhone = formatPhoneNumber(value);
    setInvoiceData(prev => ({ ...prev, telefon: formattedPhone }));
  };

  const formatPhoneNumberDisplay = (number) => {
    return number.replace(/(\d{4})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    // Taksit seçimi kontrolü - API'den taksit yüklenmediyse varsayılan 1 taksit kullan
    if (!isEftPayment && !selectedInstallment) {
      // Eğer installments dizisi boşsa veya yüklenmediyse, varsayılan 1 taksit seç
      if (!installments || installments.length === 0) {
        setSelectedInstallment(1);
        setSelectedInstallmentData({ count: 1, rate: 0, totalAmount: total });
      } else {
        setErrors(prev => ({ ...prev, submit: 'Taksit seçmediniz. Lütfen taksit seçeneğini belirtiniz.' }));
        showToast('Lütfen taksit seçeneğini belirtiniz.', 'error');
        return;
      }
    }

    // Fatura formu doğrulama
    const invoiceFormErrors = validateInvoiceFormData();
    if (Object.keys(invoiceFormErrors).length > 0) {
      setInvoiceErrors(invoiceFormErrors);

      // Her bir fatura bilgisi hatası için ayrı toast mesajı göster
      if (invoiceFormErrors.ad) {
        showToast(`Lütfen ${invoiceFormErrors.ad}`, 'error');
      }
      if (invoiceFormErrors.soyad) {
        showToast(`Lütfen ${invoiceFormErrors.soyad}`, 'error');
      }
      if (invoiceFormErrors.tckimlik) {
        showToast(`Lütfen ${invoiceFormErrors.tckimlik}`, 'error');
      }
      if (invoiceFormErrors.telefon) {
        showToast(`Lütfen ${invoiceFormErrors.telefon}`, 'error');
      }
      if (invoiceFormErrors.dogumTarihi) {
        showToast(`Lütfen ${invoiceFormErrors.dogumTarihi}`, 'error');
      }
      if (invoiceFormErrors.adres) {
        showToast(`Lütfen ${invoiceFormErrors.adres}`, 'error');
      }

      return;
    }

    // Kredi kartı için doğrulama yap
    if (!isEftPayment) {
      const formErrors = validateForm();
      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);

        // Her bir kart bilgisi hatası için ayrı toast mesajı göster
        if (formErrors.cardNumber) {
          showToast(`Lütfen ${formErrors.cardNumber}`, 'error');
        }
        if (formErrors.cardHolderName) {
          showToast(`Lütfen ${formErrors.cardHolderName}`, 'error');
        }
        if (formErrors.expiryDate) {
          showToast(`Lütfen ${formErrors.expiryDate}`, 'error');
        }
        if (formErrors.cvc) {
          showToast(`Lütfen ${formErrors.cvc}`, 'error');
        }
        if (formErrors.installment) {
          showToast(`Lütfen ${formErrors.installment}`, 'error');
        }

        return;
      }
    }

    setLoading(true);
    try {
      const { total, customerForm, cart } = location.state || {};
      const referansKodu = location.state?.referansKodu || '';

      // Fatura bilgilerini de ekle
      const updatedCustomerForm = {
        ...customerForm,
        faturaAd: invoiceData.ad,
        faturaSoyad: invoiceData.soyad,
        faturaAdres: invoiceData.adres,
        faturaDogumTarihi: invoiceData.dogumTarihi,
        faturaTckimlik: invoiceData.tckimlik,
        faturaTelefon: invoiceData.telefon,
      };

      // EFT/Havale ödeme yöntemi seçiliyse
      if (customerForm && customerForm.paymentType === 'eft') {
        fbq('track', 'InitiateCheckout');
        try {
          // const discountedTotal = total - eftDiscount;
          const eftPaymentData = {
            musteriForm: updatedCustomerForm,
            // odenecek_tutar: discountedTotal,
            odenecek_tutar: total,
            // orijinal_tutar: total,
            // indirim_tutari: eftDiscount,
            referans_kodu: referansKodu,
            payment_type: 'eft',
            isEftPayment: true
          };

          const response = await processPayment(eftPaymentData, showToast);

          if (response.success) {
            setOrderCreated(true);
            setOrderNumber(response.orderId || 'EFT-' + Date.now());
            setOrderAmount(result.price);

            // EFT siparişi başarıyla oluşturulduğunda share_ref çerezini sil
            document.cookie = "share_ref=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

            setLoading(false);
          } else {
            throw new Error(response.message || 'EFT/Havale sipariş kaydı başarısız');
          }
        } catch (error) {
          setErrors(prev => ({
            ...prev,
            submit: error.message || 'Sipariş oluşturulurken bir hata oluştu'
          }));
          setLoading(false);
        }
        return;
      }

      // Kredi kartı ödemesi için
      // Eğer selectedInstallmentData null ise varsayılan değerleri kullan
      const installmentData = selectedInstallmentData || { count: 1, rate: 0 };

      let finalAmount;
      if (hasSarrafiyeProducts && installmentData.count === 1) {
        // 1. taksitte sarrafiye komisyonu ekle
        const sarrafiyeCommission = total * 0.04;
        finalAmount = total + sarrafiyeCommission;
      } else {
        // Diğer taksitlerde sadece banka komisyonu
        finalAmount = total * (1 + (installmentData.rate / 100));
      }

      const paymentData = {
        kart_numarasi: cardNumber,
        isim: cardHolderName,
        son_kullanma: expiryDate,
        ccv: cvc,
        taksit_sayisi: installmentData.count,
        odenecek_tutar: finalAmount,
        musteriForm: updatedCustomerForm,
        referans_kodu: referansKodu,
      };

      fbq('track', 'Purchase', {
        currency: 'TRY',
        value: total
      });

      const response = await processPayment(paymentData, showToast);

      if (response.success) {
        if (!response.redirect_url) {
          setErrors(prev => ({
            ...prev,
            submit: 'Banka kartlarına taksit yapılamamaktadır.'
          }));
          return;
        }

        // Share_ref çerezini sil
        document.cookie = "share_ref=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        if (response.redirect_url) {
          window.location.replace(response.redirect_url);
          return;
        } else {
          navigate('/success', {
            state: {
              orderId: response.orderId,
              amount: installmentData.totalAmount || total
            }
          });
        }
      } else {
        // Başarısız ödeme durumunda share_ref çerezini silme
        throw new Error(response.message || 'Ödeme işlemi başarısız');
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Ödeme işlemi sırasında bir hata oluştu'
      }));
    } finally {
      setLoading(false);
    }
  };

  const defaultInstallments = [
    { count: 1, totalAmount: total },
    { count: 2, totalAmount: total },
    { count: 3, totalAmount: total },
  ];

  // Metin kopyalama fonksiyonu
  const copyToClipboard = (text, label) => {
    try {
      // Yardımcı textarea elementi oluştur
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // Görünmez yap
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);

      // Seç ve kopyala
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setCopySuccess(`${label} kopyalandı!`);
        setTimeout(() => setCopySuccess(''), 2000);
      } else {
        // execCommand başarısız olursa modern API'yi dene
        navigator.clipboard.writeText(text)
          .then(() => {
            setCopySuccess(`${label} kopyalandı!`);
            setTimeout(() => setCopySuccess(''), 2000);
          })
          .catch(err => {
            setCopySuccess('Kopyalama başarısız!');
            console.error('Kopyalama hatası:', err);
          });
      }
    } catch (err) {
      setCopySuccess('Kopyalama başarısız!');
      console.error('Kopyalama hatası:', err);
    }
  };

  // EFT ödeme bildirimi fonksiyonu
  const handleConfirmEftPayment = async () => {
    try {
      if (!orderNumber) {
        console.error('Sipariş numarası bulunamadı');

        showToast('Sipariş numarası bulunamadı. Lütfen tekrar deneyin.', 'error');
        return;
      }
      setLoading(true);
      const result = await confirmEftPayment(orderNumber, orderAmount, showToast);

      if (result.success) {
        setPaymentState({
          success: true,
          orderId: orderNumber,
          amount: result.price
        });

      } else {
        // Toast mesajı zaten confirmEftPayment içinde gösteriliyor.
      }
    } catch (error) {
      console.error('EFT ödeme bildirimi yapılırken hata:', error);
      showToast('EFT bildirimi yapılırken bir hata oluştu.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // EFT ödeme onaylama için useEffect
  useEffect(() => {
    // orderNumber değeri varsa ve orderCreated true ise
    if (orderNumber && orderCreated && isEftPayment) {
      handleConfirmEftPayment();
    }
  }, [orderNumber, orderCreated, isEftPayment]);

  // Fatura adı ve soyadı değiştiğinde kart sahibi adını otomatik güncelle
  useEffect(() => {
    if (invoiceData.ad || invoiceData.soyad) {
      const fullName = `${invoiceData.ad} ${invoiceData.soyad}`.trim();
      if (fullName) {
        setCardHolderName(fullName);
      }
    }
  }, [invoiceData.ad, invoiceData.soyad]);

  const handlePaymentComplete = () => {
    setIsPaymentCompleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsPaymentCompleteModalOpen(false);
    navigate('/');
  };

  if (loading) return <div className='loader-info'>
    <p className='m-0'>İşlem alınıyor...</p>
    <Loader />
  </div>;
  { error && <p>Hata: {error}</p> }
  { paymentSuccess && <p>Ödeme başarıyla gerçekleşti!</p> }
  return (
    <>
      <div className='payment-title'>
        <h2 className='my-3'>Ödeme Sayfası</h2>

        {/* Fatura Bilgileri Formu - EFT ödemesi ve sipariş oluşturulmuşsa gösterme */}
        {!(isEftPayment && orderCreated) && (
          <div>
            <div>
              <div className="payment-information-header">
                <h3>Fatura Bilgileri</h3>
              </div>
              {
                !isEftPayment ?
                  <div>
                    <p className="invoice-info-text"><strong>Fatura bilgileri</strong> kredi kartı sahibi ile <strong>aynı olmalıdır</strong></p>

                    <p className='invoice-info-text'>Son ekrana gelene kadar ürün fiyatları kurlar ile birlikte <strong>güncellenmektedir.</strong> Son ulaştığınız ekrandaki fiyat geçerlidir.</p>
                  </div>
                  :
                  <div className='invoice-group'>
                    <p className="invoice-info-text"><strong>Ücretin gönderildiği hesap</strong> fatura bilgisi ile <strong>uymalıdır</strong>.
                      Aksi takdirde <strong>siparişiniz iptal edilecektir</strong></p>

                    <p className='invoice-info-text'>Son ekrana gelene kadar ürün fiyatları kurlar ile birlikte <strong>güncellenmektedir.</strong> Son ulaştığınız ekrandaki fiyat <strong>10 dakika</strong> için geçerlidir.</p>

                    <p className='invoice-info-text'><strong>10 dakika</strong> içinde ödeme <strong>gerçekleştirilmezse</strong> fiyatlar <strong>anlık</strong> kurla beraber güncellenecektir</p>
                    
                    {/* <div className="eft-discount-info" style={{
                      backgroundColor: '#e8f5e8',
                      border: '1px solid #4caf50',
                      padding: '15px',
                    }}>
                      <p style={{ margin: '0', color: '#2e7d32', fontWeight: 'bold' }}>
                        🎉 EFT/Havale ile ödeme yaparak %2 indirim kazandınız!
                      </p>
                    </div> */}
                  </div>
              }
            </div>
            <div className="invoice-information">
              <div className="invoice-form">
                <div className="form-group">
                  <label>Ad*</label>
                  <input
                    type="text"
                    name="ad"
                    value={invoiceData.ad}
                    onChange={handleInvoiceChange}
                    placeholder="Adınız"
                    required
                  />
                  {invoiceErrors.ad && <span className="error">{invoiceErrors.ad}</span>}
                </div>

                <div className="form-group">
                  <label>Soyad*</label>
                  <input
                    type="text"
                    name="soyad"
                    value={invoiceData.soyad}
                    onChange={handleInvoiceChange}
                    placeholder="Soyadınız"
                    required
                  />
                  {invoiceErrors.soyad && <span className="error">{invoiceErrors.soyad}</span>}
                </div>

                <div className="form-group">
                  <label>TC Kimlik No*</label>
                  <input
                    type="text"
                    name="tckimlik"
                    value={invoiceData.tckimlik}
                    onChange={handleInvoiceChange}
                    placeholder="TC Kimlik No"
                    maxLength="11"
                    required
                  />
                  {invoiceErrors.tckimlik && <span className="error">{invoiceErrors.tckimlik}</span>}
                </div>

                <div className="form-group">
                  <label>Telefon*</label>
                  <input
                    type="text"
                    name="telefon"
                    value={invoiceData.telefon}
                    onChange={handleInvoiceChange}
                    placeholder="05XX XXX XX XX"
                    maxLength="11"
                    required
                  />
                  {invoiceErrors.telefon && <span className="error">{invoiceErrors.telefon}</span>}
                </div>

                <div className="form-group">
                  <label>Doğum Tarihi*</label>
                  <input
                    type="text"
                    name="dogumTarihi"
                    value={invoiceData.dogumTarihi}
                    onChange={handleInvoiceChange}
                    placeholder="GG/AA/YYYY"
                    maxLength="10"
                    required
                  />
                  {invoiceErrors.dogumTarihi && <span className="error">{invoiceErrors.dogumTarihi}</span>}
                </div>

                <div className="form-group">
                  <label>Adres*</label>
                  <textarea
                    name="adres"
                    value={invoiceData.adres}
                    onChange={handleInvoiceChange}
                    placeholder="Fatura adresi"
                    required
                  />
                  {invoiceErrors.adres && <span className="error">{invoiceErrors.adres}</span>}
                </div>
              </div>
              {!isEftPayment && (
                <div className={`payment-page ${isFormActive ? "form-active" : ""}`}>
                  <div className="payment-container">
                    <div className="payment-form">
                      <div className="commission-info">
                        <button onClick={() => setIsOpen(true)}>Komisyon Oranları</button>
                        {commissionRate.length > 0 ? (
                          <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)} contentLabel="Komisyon Oranları" className="ReactModal__Content"
                            overlayClassName="ReactModal__Overlay">
                            <h2>Komisyon Oranları</h2>
                            <button className="close-button" onClick={() => setIsOpen(false)}>
                              &times;</button>
                            <table>
                              <thead>
                                <tr>
                                  <th>Banka</th>
                                  <th>1 Taksit</th>
                                  <th>2 Taksit</th>
                                  <th>3 Taksit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {commissionRate.map((bank, i) => {
                                  // if (bank.name == "Şekerbank CC") return null;

                                  const taksit1 = bank.taksit.find((t) => t.count == 1)?.rate ?? '';
                                  let taksit2 = bank.taksit.find((t) => t.count == 2)?.rate ?? '';
                                  let taksit3 = bank.taksit.find((t) => t.count == 3)?.rate ?? '';
                                  taksit2 = parseFloat(taksit2).toFixed(2)
                                  taksit3 = parseFloat(taksit3).toFixed(2)

                                  return (
                                    <tr key={i}>
                                      <td>{bank.name}</td>
                                      {/* <td>{bank.name}<img className='bank-img' src={bank.image} alt={bank.name} /></td> */}
                                      <td>{taksit1}</td>
                                      <td>{taksit2}%</td>
                                      <td>{taksit3}%</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </Modal>
                        ) : (
                          <p>Komisyon oranı mevcut değil.</p>
                        )}
                      </div>

                      <form onSubmit={handlePaymentSubmit}>
                        <div className="form-group">
                          <label>Kart Numarası:</label>
                          <input
                            type="text"
                            value={cardNumber}
                            onChange={handleCardNumberChange}
                            maxLength="16"
                            placeholder="Kart numaranızı giriniz"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Kart Üzerindeki Ad Soyad:</label>
                          <input
                            type="text"
                            value={cardHolderName}
                            onChange={handleCardHolderNameChange}
                            placeholder="Kartın üzerindeki ad soyad"
                            required
                            disabled
                          />
                        </div>

                        <div className="form-row d-flex gap-1">
                          <div className="small-group d-flex flex-column">
                            <label>Tarihi:</label>
                            <input
                              type="text"
                              value={expiryDate}
                              onChange={handleExpiryDateChange}
                              placeholder="AA/YY"
                              maxLength="5"
                              required
                            />
                          </div>
                          <div className="small-group d-flex flex-column">
                            <label>CVC:</label>
                            <input
                              type="text"
                              value={cvc}
                              onChange={handleCvcChange}
                              placeholder="CVC"
                              maxLength="3"
                              required
                            />
                          </div>
                        </div>

                        {cardNumber.length >= 8 && (
                          <div className="installment-options">
                            <h3>Taksit Seçenekleri</h3>
                            <table className="installment-table">
                              <thead>
                                <tr>
                                  <th>Taksit Sayısı</th>
                                  <th>Aylık Ödeme</th>
                                  <th>Banka Komisyonu</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // API'den taksit seçenekleri yüklenmediyse varsayılan seçenekleri kullan
                                  const availableInstallments = (installments && installments.length > 0)
                                    ? installments
                                    : [{ count: 1, rate: 0, totalAmount: total }];

                                  return availableInstallments.map((installment) => {
                                    let displayAmount;

                                    if (hasSarrafiyeProducts && installment.count === 1) {
                                      // 1. taksitte sarrafiye komisyonu ekle
                                      const sarrafiyeCommission = total * 0.04;
                                      const adjustedTotal = total + sarrafiyeCommission;
                                      displayAmount = adjustedTotal.toFixed(2);
                                    } else {
                                      // Diğer taksitlerde sadece banka komisyonu
                                      displayAmount = calculateMonthlyPayment(total, installment.count, installment.rate);
                                    }

                                    return (
                                      <tr key={installment.count}>
                                        <td> <label>
                                          <input
                                            type="radio"
                                            name="installment"
                                            value={installment.count}
                                            checked={selectedInstallment === installment.count}
                                            onChange={handleInstallmentChange}
                                          />
                                          {installment.count} Taksit</label>
                                        </td>
                                        <td>
                                          <span>{displayAmount} TL x {installment.count} </span>
                                        </td>
                                        <td>
                                          <span>
                                            {hasSarrafiyeProducts && installment.count === 1 ? '4' : (installment.rate || 0)} %
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                            </table>
                            
                            {/* Sarrafiye komisyonu bilgilendirmesi */}
                            {hasSarrafiyeProducts && (
                              <div className="sarrafiye-commission-info" style={{
                                backgroundColor: '#e0f0ffff',
                                border: '1px solid #9fcaf1ff',
                                borderRadius: '5px',
                                padding: '10px',
                                marginTop: '15px',
                                fontSize: '14px',
                                color: '#425A8B'
                              }}>
                                <p style={{ margin: '0', fontWeight: 'bold' }}>
                                  ℹ️ Sarrafiye ürünlerinde 1. taksitte %4 komisyon yansıtılacaktır
                                </p>
                              </div>
                            )}
                            
                            {errors.submit && (
                              <p className="payment-error-message">
                                {errors.submit}
                              </p>
                            )}
                          </div>
                        )}

                        <button type="submit" disabled={loading} className='payment-button'>
                          {loading ? 'İşlem Yapılıyor...' : 'Ödeme Yap'}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ödeme Bilgileri Sekmesi */}
        <div className="payment-information-section">
          <div className="">
            {/* EFT veya Kart Bilgileri */}
            {isEftPayment && (
              <div>
                {!orderCreated ? (
                  // Sipariş oluşturulmadan önce
                  <div className='eft-confirm-container'>
                    <p>Siparişinizi oluşturmak için aşağıdaki butona tıklayın. Sonrasında ödeme bilgileriniz görüntülenecektir.</p>
                    <button
                      onClick={handlePaymentSubmit}
                      className="eft-confirm-button"
                      disabled={loading}
                    >
                      {loading ? 'İşlem Yapılıyor...' : 'Siparişi Oluştur'}
                    </button>
                    {errors.submit && (
                      <div className="eft-error-message">
                        <p>{errors.submit}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Sipariş oluşturulduktan sonra
                  <div>
                    <h3>EFT/Havale ile Ödeme - Sipariş Onayınız</h3>
                    <p>Siparişiniz başarıyla oluşturuldu. Aşağıdaki banka hesabına ödemenizi yapınız. Ödeme sonrası siparişiniz onaylanarak işleme alınacaktır.</p>

                    <div className="order-details">
                      <p>
                        <strong>Sipariş Numarası:</strong> {orderNumber}
                      </p>
                      <p><strong>Ödenecek Tutar:</strong> {(total).toFixed(2)} TL</p>
                      <p className='order-details-info'>
                        10 Dakika içerisinde ödeme yapmazsanız fiyat güncellenecektir.
                      </p>
                    </div>

                    <div className="bank-account-info">
                      <div>
                        <p><strong>Banka:</strong></p>
                        <p>{eftData ? eftData.bank : 'Yükleniyor...'}</p>
                      </div>
                      <div>
                        <p><strong>IBAN:</strong></p>
                        <p>
                          {eftData ? eftData.iban : 'Yükleniyor...'}
                          <button
                            className="copy-button"
                            onClick={() => copyToClipboard(eftData ? eftData.iban : '', 'IBAN')}
                          >
                            <span>📋</span>
                          </button>
                        </p>
                      </div>
                      <div>
                        <p><strong>Hesap Sahibi:</strong></p>
                        <p>
                          {eftData ? eftData.name : 'Yükleniyor...'}
                          <button
                            className="copy-button"
                            onClick={() => copyToClipboard(eftData ? eftData.name : '', 'Hesap sahibi')}
                          >
                            <span>📋</span>
                          </button>
                        </p>
                      </div>
                      <div>
                        <p><strong>Açıklama:</strong></p>
                        <p>
                          {orderNumber} Nolu Sipariş
                          <button
                            className="copy-button"
                            onClick={() => copyToClipboard(`${orderNumber} Nolu Sipariş`, 'Açıklama')}
                          >
                            <span>📋</span>
                          </button>
                        </p>
                      </div>
                    </div>

                    {copySuccess && <div className="copy-success-message">{copySuccess}</div>}

                    <div className="eft-important-notice">
                      <div className="siparis-no-uyarisi">
                        <h3>⚠️ ÖNEMLİ UYARI ⚠️</h3>
                        <p className="buyuk-uyari">Ödeme yaparken <span className="kirmizi-yazi">AÇIKLAMA KISMINA</span> mutlaka <span className="kirmizi-yazi">SİPARİŞ NUMARANIZI</span> yazınız!</p>
                        <p className="ikinci-uyari">Açıklama kısmına sipariş numarası yazmayan ödemeler eşleştirilemez ve <span className="kalin-yazi">siparişiniz iptal edilir!</span></p>
                        <div className="siparis-no-kutusu">
                          <p>Sipariş Numaranız: <span className="siparis-no-vurgu">{orderNumber}</span></p>
                          <div className="siparis-no-alt-uyari">
                            <p>⚠️ <span className="kirmizi-yazi">BU NUMARAYI BANKANIZIN AÇIKLAMA KISMINA YAZMAZSANIZ SİPARİŞİNİZ İPTAL EDİLİR!</span> ⚠️</p>
                          </div>
                        </div>
                      </div>
                      <p>Ödemeniz kontrol edildikten sonra siparişiniz onaylanacaktır.</p>
                      <p>Sipariş sahibi: <strong>{customerForm?.ad} {customerForm?.soyad}</strong></p>
                    </div>
                    <button
                      className="btn d-flex align-items-center justify-content-center gap-2 mt-4 mx-auto"
                      style={{
                        maxWidth: '200px',
                        backgroundColor: '#425A8B',
                        color: '#fff'
                      }}
                      onClick={handlePaymentComplete}
                    >
                      Ödemeyi Tamamladım
                    </button>

                    <Modal
                      isOpen={isPaymentCompleteModalOpen}
                      onRequestClose={handleCloseModal}
                      contentLabel="Ödeme Tamamlandı"
                      className="ReactModal__Content"
                      overlayClassName="ReactModal__Overlay"
                    >
                      <div className="text-center">
                        <h3 className="mb-3">Teşekkürler!</h3>
                        <p className="mb-4">Siparişiniz alındı. Ekibimiz en kısa süre içinde kontrol edip onaylayacaktır.</p>
                        <button
                          className="btn btn-primary"
                          onClick={handleCloseModal}
                          style={{ backgroundColor: '#425A8B' }}
                        >
                          Tamam
                        </button>
                      </div>
                    </Modal>

                  </div>

                )}

              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
};

export default Payment;